// @flow
import FileInfoUtil from './FileInfoUtil';
import _ from 'lodash';

const debug = require('../debug/debug')('wc:util:DndUtil');

// Nonstandard API so editors doesn't provide autocomplete
type FileSystemEntry = {
  isFile: boolean,
  isDirectory: boolean,
  name: string,
  fullPath: string,
  filesystem: Object,
  file: Function,
  createReader: Function
};

export function getFilesFromDropEvent(e: DragEvent): Promise<File[]> {
  debug('[getFilesFromDropEvent()] event: ', e);

  if (
    e.dataTransfer &&
    e.dataTransfer.items &&
    window.DataTransferItem &&
    window.DataTransferItem.prototype.webkitGetAsEntry
  ) {
    // Newer APIs allow DnD of folders. Safari still doesn't support this as of Sept 2017.
    debug('[getFilesFromDropEvent()] browser supports directory uploads. Calling processFilesAndFolders()');
    return processFilesAndFolders(e.dataTransfer.items);
  }

  debug('[getFilesFromDropEvent()] browser does not support directory uploads. Calling processFiles()');
  const files = e.dataTransfer ? e.dataTransfer.files : e.target.files;
  return Promise.resolve(processFiles(files));
}

const processFilesAndFolders = (items: DataTransferItemList): Promise<File[]> => {
  return new Promise((resolve, reject) => {
    debug('[processFilesAndFolders()] begin');
    const files = [];

    // When an async operation start increment the counter, when it finishes decrement
    // when counter is 0 it means that we have red all the items recursively to the very bottom and we need to handle the result
    let counter = 0;

    for (const item of items) {
      const entry: FileSystemEntry = item.webkitGetAsEntry();
      if (!entry) {
        debug('[processFilesAndFolders()] entry is null. continue.');
        continue;
      }

      if (entry.isDirectory) {
        processFolder(entry);
      } else {
        counter++;
        entry.file(processFile);
      }
    }

    function processFolder(entry: FileSystemEntry) {
      debug('[processFilesAndFolders()] [processFolder()] entry: ', entry);
      const reader = entry.createReader();
      counter++;

      const readChunk = () => {
        reader.readEntries((entries: FileSystemEntry[]) => {
          counter--;

          if (entries.length) {
            for (const entry of entries) {
              if (entry.isDirectory) {
                processFolder(entry);
              } else {
                counter++;
                entry.file(processFile);
              }
            }

            //readChunk();
          }

          if (counter == 0) {
            debug('[processFilesAndFolders()] [processFolder()] done. files: ', files);
            resolve(files);
          }
        });
      };

      readChunk();
    }

    function processFile(file) {
      debug('[processFilesAndFolders()] [processFile()] file: ', file);
      counter--;
      let fileToIgnore = _.indexOf(FileInfoUtil.ignoreFileNames, file.name) > -1;

      // if (!fileToIgnore) {
      //   const ext = FileInfoUtil.getFileExtension(file.name);
      //   fileToIgnore = ext.length > 0 && _.indexOf(FileInfoUtil.ignoreFileTypes, ext) > -1;
      // }

      if (fileToIgnore) {
        debug('[processFilesAndFolders()] [processFile()] file blacklisted, not adding to results array');
      } else {
        files.push(file);
      }

      if (counter == 0) {
        debug('[processFilesAndFolders()] [processFile()] done. files: ', files);
        resolve(files);
      }
    }
  });
};

function processFiles(files: FileList): File[] {
  const results = [...files].filter((f: File) => _.indexOf(FileInfoUtil.ignoreFileNames, f.name) === -1);
  debug('[processFiles()] files: ', results);
  return results;
}

/**
 * Chrome exposes files on dragenter, other browsers don't expose files until drop.
 * So we do our best effort to determine if a file is being dragged.
 */
export function dragEventContainsFiles(event: DragEvent) {
  debug('[dragEventContainsFiles()] event: ', event);
  if (!(event && event.dataTransfer)) {
    // This isn't a drag event.
    return false;
  }

  const { types = [], files = [], items = [] } = event.dataTransfer;

  // Chrome is easy to check.
  if (window.chrome) {
    return _.includes(types, 'Files');
  }

  // types is the primary way of interrogating a drag event.
  if (types.length) {
    return _.includes(types, 'Files');
  }

  // Older browsers don't have types. Use old school checks.

  if (files.length > 0) {
    // There are files.
    return true;
  }

  const filteredItems = [...items].filter((i) => i.kind === 'file');
  if (filteredItems.length > 0) {
    // There are items with { kind: 'file' }
    return true;
  }

  if (files.length === 0 && items.length === 0) {
    // If there is no data at all in the event, assume the browser is hiding
    // files until drop.
    return true;
  }

  return false;
}
